import { type ChecklistItem, type Surgery, type SurgeryRecordFields, type SurgerySummary, TEAM_ROLES } from '@dnx/contracts';
import { Alert, Badge, Button, Card, CardHeader, Dialog, Field, Input, Select, Spinner, cn } from '@dnx/ui';
import { queryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, CheckCircle2, Circle, FileSignature, Lock, MinusCircle, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { professionalsQuery } from '../agenda/queries.js';
import { fieldErrors } from '../clinical/patients-page.js';
import { api } from '../lib/api.js';
import { meQuery } from '../lib/session.js';

export const surgeriesQuery = (patientId?: string) => queryOptions({
  queryKey: ['surgeries', 'list', patientId ?? ''], queryFn: () => api<SurgerySummary[]>(`/surgeries${patientId ? `?paciente=${patientId}` : ''}`),
});
const surgeryQuery = (id: string) => queryOptions({ queryKey: ['surgeries', id], queryFn: () => api<Surgery>(`/surgeries/${id}`) });
export const SURGERY_STATUS: Record<Surgery['status'], [string, 'neutral' | 'primary' | 'success' | 'warning']> = {
  planning: ['Em preparação', 'warning'], scheduled: ['Agendada', 'primary'], done: ['Realizada', 'success'], cancelled: ['Cancelada', 'neutral'],
};
const ROLE_LABEL: Record<(typeof TEAM_ROLES)[number], string> = { surgeon: 'Cirurgião', assistant: 'Auxiliar', nurse: 'Enfermagem', technician: 'Técnico', anesthesiologist: 'Anestesiologista', other: 'Outro' };
const AREA_LABEL: Record<string, string> = { frontal: 'Frontal', hairline: 'Linha frontal', mid_scalp: 'Região média', crown: 'Coroa', temples: 'Entradas/têmporas', beard: 'Barba', eyebrow: 'Sobrancelha', other: 'Outra' };
const dt = (iso: string) => new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const localInput = (iso: string | null) => (iso ? new Date(new Date(iso).getTime() - 3 * 3_600_000).toISOString().slice(0, 16) : '');
const fromLocal = (v: string) => (v ? new Date(`${v}:00-03:00`).toISOString() : null);

function usePerms() {
  const { data: me } = useSuspenseQuery(meQuery);
  const p = new Set(me.permissions);
  return { write: p.has('clinical.record.write'), sign: p.has('clinical.record.sign'), schedule: p.has('scheduling.surgery.schedule') };
}

function Checklist({ s }: { s: Surgery }) {
  const qc = useQueryClient();
  const { write } = usePerms();
  const set = useMutation({
    mutationFn: ({ key, state }: { key: string; state: 'done' | 'not_applicable' | 'pending' }) => api<Surgery>(`/surgeries/${s.id}/checklist/${key}`, { method: 'PATCH', body: { state } }),
    onSuccess: (n) => qc.setQueryData(['surgeries', s.id], n),
  });
  const open = s.status === 'planning' || s.status === 'scheduled';
  const icon = (c: ChecklistItem) => c.state === 'done' ? <CheckCircle2 className="size-4 text-success" /> : c.state === 'not_applicable' ? <MinusCircle className="size-4 text-fg-subtle" /> : <Circle className={cn('size-4', c.required ? 'text-warning' : 'text-fg-subtle')} />;
  return (
    <Card>
      <CardHeader title="Checklist pré-operatório" description="Os itens automáticos o sistema confere sozinho. Os marcados como obrigatórios liberam o agendamento." />
      <ul className="divide-y divide-border">
        {s.checklist.map((c) => (
          <li key={c.key} className="flex flex-wrap items-center gap-3 px-5 py-3">
            {icon(c)}
            <div className="min-w-0 flex-1">
              <p className="text-sm text-fg">{c.label}{c.required && <span className="text-[12px] text-fg-subtle"> · obrigatório</span>}</p>
              <p className="text-[12px] text-fg-muted">
                {c.auto ? 'automático' : c.doneByName ? `${c.doneByName}${c.doneAt ? ` · ${new Date(c.doneAt).toLocaleDateString('pt-BR')}` : ''}` : 'manual'}{c.detail ? ` · ${c.detail}` : ''}
              </p>
              {c.warning && <p className="mt-0.5 flex items-center gap-1 text-[12px] font-medium text-warning"><AlertTriangle className="size-3.5" />{c.warning}</p>}
            </div>
            {!c.auto && write && open && (
              c.state === 'pending' ? (
                <div className="flex gap-1">
                  <Button size="sm" variant="secondary" loading={set.isPending && set.variables?.key === c.key} onClick={() => set.mutate({ key: c.key, state: 'done' })}>Feito</Button>
                  {!c.required && <Button size="sm" variant="ghost" onClick={() => set.mutate({ key: c.key, state: 'not_applicable' })}>Não se aplica</Button>}
                </div>
              ) : <Button size="sm" variant="ghost" onClick={() => set.mutate({ key: c.key, state: 'pending' })}>Desfazer</Button>
            )}
          </li>
        ))}
      </ul>
      {set.error && <Alert tone="danger" className="m-4">{set.error.message}</Alert>}
    </Card>
  );
}

function ScheduleCard({ s }: { s: Surgery }) {
  const qc = useQueryClient();
  const { schedule } = usePerms();
  const { data: pros = [] } = useQuery(professionalsQuery);
  const [pro, setPro] = useState('');
  const [at, setAt] = useState(localInput(s.appointment?.startsAt ?? null));
  const [hours, setHours] = useState(s.appointment ? String(Math.round((Date.parse(s.appointment.endsAt) - Date.parse(s.appointment.startsAt)) / 3_600_000)) : '7');
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const book = useMutation({
    mutationFn: () => api<Surgery>(`/surgeries/${s.id}/schedule`, { method: 'POST', body: { professionalId: pro || pros[0]?.id, startsAt: fromLocal(at), durationMin: Math.round(Number(hours) * 60), notifyPatient: true } }),
    onSuccess: (n) => { qc.setQueryData(['surgeries', s.id], n); void qc.invalidateQueries({ queryKey: ['surgeries'] }); },
  });
  const cancel = useMutation({
    mutationFn: () => api<Surgery>(`/surgeries/${s.id}/cancel`, { method: 'POST', body: { reason } }),
    onSuccess: (n) => { qc.setQueryData(['surgeries', s.id], n); setCancelling(false); },
  });
  const open = s.status === 'planning' || s.status === 'scheduled';
  return (
    <Card>
      <CardHeader title="Agenda" />
      <div className="grid gap-4 p-5">
        {s.appointment && <p className="flex items-center gap-2 text-sm text-fg"><CalendarClock className="size-4 text-primary" />{dt(s.appointment.startsAt)} até {new Date(s.appointment.endsAt).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' })}{s.appointment.professionalName ? ` · ${s.appointment.professionalName}` : ''}</p>}
        {!s.unitEnabled && <Alert tone="warning" title="Unidade sem habilitação para cirurgia">Configure o tipo de estabelecimento (II a IV) e o responsável técnico em Configurações › Unidade.</Alert>}
        {!s.ready && open && <Alert tone="info" title="Falta para agendar">{s.missing.join(' · ')}</Alert>}
        {open && schedule && (
          <div className="grid gap-3 sm:grid-cols-[1fr_110px] sm:items-end">
            <div className="sm:col-span-2"><Field label="Cirurgião"><Select value={pro || pros[0]?.id || ''} onChange={(e) => setPro(e.target.value)}>{pros.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</Select></Field></div>
            <Field label="Início"><Input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} /></Field>
            <Field label="Duração (h)"><Input type="number" min={1} max={16} value={hours} onChange={(e) => setHours(e.target.value)} /></Field>
            <div className="sm:col-span-2"><Button disabled={!s.ready || !s.unitEnabled || !at} loading={book.isPending} onClick={() => book.mutate()}>{s.appointment ? 'Remarcar' : 'Agendar cirurgia'}</Button></div>
          </div>
        )}
        {book.error && <Alert tone="danger">{book.error.message}</Alert>}
        {open && schedule && (cancelling ? (
          <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); cancel.mutate(); }}>
            <Field label="Motivo do cancelamento"><Input value={reason} onChange={(e) => setReason(e.target.value)} className="w-72" /></Field>
            <Button type="submit" variant="danger" disabled={reason.trim().length < 3} loading={cancel.isPending}>Cancelar cirurgia</Button>
            <Button variant="ghost" onClick={() => setCancelling(false)}>Voltar</Button>
          </form>
        ) : <div><Button size="sm" variant="ghost" onClick={() => setCancelling(true)}>Cancelar caso</Button></div>)}
        {s.cancelReason && <p className="text-sm text-fg-muted">Cancelado: {s.cancelReason}</p>}
      </div>
    </Card>
  );
}

const EMPTY: SurgeryRecordFields = { technique: 'FUE', anesthesiaType: 'local', anesthesiologist: null, graftsExtracted: null, graftsImplanted: null, team: [], areas: [], startedAt: null, endedAt: null, complications: '', notes: '' };

function RecordCard({ s }: { s: Surgery }) {
  const qc = useQueryClient();
  const { write, sign } = usePerms();
  const r = s.record;
  const [f, setF] = useState<SurgeryRecordFields>(r?.fields ?? { ...EMPTY, technique: (s.plan.technique as SurgeryRecordFields['technique']) ?? 'FUE' });
  const [dirty, setDirty] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [addendum, setAddendum] = useState('');
  const locked = r?.status === 'signed' || !write;
  const set = <K extends keyof SurgeryRecordFields>(k: K, v: SurgeryRecordFields[K]) => { setF((x) => ({ ...x, [k]: v })); setDirty(true); };
  const done = (n: Surgery) => { qc.setQueryData(['surgeries', s.id], n); void qc.invalidateQueries({ queryKey: ['surgeries'] }); };
  const save = useMutation({ mutationFn: () => api<Surgery>(`/surgeries/${s.id}/record`, { method: 'PUT', body: f }), onSuccess: (n) => { done(n); setDirty(false); } });
  const doSign = useMutation({ mutationFn: () => api<Surgery>(`/surgeries/${s.id}/record/sign`, { method: 'POST' }), onSuccess: (n) => { done(n); setConfirm(false); } });
  const add = useMutation({ mutationFn: () => api<Surgery>(`/surgeries/${s.id}/record/addenda`, { method: 'POST', body: { text: addendum } }), onSuccess: (n) => { done(n); setAddendum(''); } });
  const errs = { ...fieldErrors(save.error), ...fieldErrors(doSign.error) };
  const num = (v: string) => (v === '' ? null : Number(v));
  if (s.status === 'planning' || s.status === 'cancelled') return null;
  return (
    <Card>
      <CardHeader title="Registro da cirurgia" description="O que foi feito no procedimento. Depois de assinado pelo médico não muda; correções entram como adendo." />
      <div className="grid gap-4 p-5">
        {r?.status === 'signed' && <p className="flex items-center gap-2 rounded-md border border-success/25 bg-success-soft px-3.5 py-2.5 text-sm text-fg"><ShieldCheck className="size-4 text-success" />Assinado por <strong>{r.signedByName}</strong> em {r.signedAt && dt(r.signedAt)}.</p>}
        <div className="grid gap-4 sm:grid-cols-4">
          <Field label="Técnica"><Select value={f.technique} disabled={locked} onChange={(e) => set('technique', e.target.value as SurgeryRecordFields['technique'])}>{['FUE', 'FUT', 'DHI', 'other'].map((t) => <option key={t} value={t}>{t === 'other' ? 'Outra' : t}</option>)}</Select></Field>
          <Field label="Anestesia"><Select value={f.anesthesiaType} disabled={locked} onChange={(e) => set('anesthesiaType', e.target.value as SurgeryRecordFields['anesthesiaType'])}>
            <option value="local">Local</option><option value="sedation">Sedação</option><option value="general">Geral</option></Select></Field>
          <Field label="Enxertos extraídos"><Input type="number" min={0} value={f.graftsExtracted ?? ''} disabled={locked} onChange={(e) => set('graftsExtracted', num(e.target.value))} /></Field>
          <Field label="Enxertos implantados" error={errs.graftsImplanted}><Input type="number" min={0} value={f.graftsImplanted ?? ''} disabled={locked} onChange={(e) => set('graftsImplanted', num(e.target.value))} /></Field>
        </div>
        {f.anesthesiaType !== 'local' && <Field label="Anestesiologista (nome e CRM)" error={errs.anesthesiologist}><Input value={f.anesthesiologist ?? ''} disabled={locked} onChange={(e) => set('anesthesiologist', e.target.value || null)} /></Field>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Início"><Input type="datetime-local" value={localInput(f.startedAt)} disabled={locked} onChange={(e) => set('startedAt', fromLocal(e.target.value))} /></Field>
          <Field label="Término" error={errs.endedAt}><Input type="datetime-local" value={localInput(f.endedAt)} disabled={locked} onChange={(e) => set('endedAt', fromLocal(e.target.value))} /></Field>
        </div>
        <div>
          <p className="text-[13px] font-medium text-fg">Regiões tratadas</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {Object.entries(AREA_LABEL).map(([k, l]) => {
              const on = f.areas.includes(k as never);
              return <button key={k} type="button" disabled={locked} aria-pressed={on} onClick={() => set('areas', (on ? f.areas.filter((x) => x !== k) : [...f.areas, k]) as SurgeryRecordFields['areas'])}
                className={cn('h-8 rounded-full border px-3 text-[13px] disabled:cursor-default', on ? 'border-primary bg-primary-soft text-primary' : 'border-border-strong text-fg-muted')}>{l}</button>;
            })}
          </div>
        </div>
        <div>
          <p className="text-[13px] font-medium text-fg">Equipe</p>
          <ul className="mt-2 grid gap-2">
            {f.team.map((m, i) => (
              <li key={i} className="grid grid-cols-[1fr_180px_36px] gap-2">
                <Input aria-label="Nome" value={m.name} disabled={locked} onChange={(e) => set('team', f.team.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                <Select aria-label="Função" value={m.role} disabled={locked} onChange={(e) => set('team', f.team.map((x, j) => (j === i ? { ...x, role: e.target.value as typeof m.role } : x)))}>
                  {TEAM_ROLES.map((r2) => <option key={r2} value={r2}>{ROLE_LABEL[r2]}</option>)}
                </Select>
                {!locked && <Button size="icon" variant="ghost" aria-label="Remover" onClick={() => set('team', f.team.filter((_, j) => j !== i))}><Trash2 className="size-4" /></Button>}
              </li>
            ))}
          </ul>
          {!locked && <Button size="sm" variant="ghost" className="mt-1" onClick={() => set('team', [...f.team, { name: '', role: 'assistant' }])}><Plus className="size-4" />Pessoa da equipe</Button>}
        </div>
        <Field label="Intercorrências"><textarea value={f.complications} disabled={locked} onChange={(e) => set('complications', e.target.value)} rows={2} className="w-full resize-y rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-fg disabled:bg-surface-muted/60" /></Field>
        <Field label="Descrição / observações" hint="Cifradas."><textarea value={f.notes} disabled={locked} onChange={(e) => set('notes', e.target.value)} rows={4} className="w-full resize-y rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-fg disabled:bg-surface-muted/60" /></Field>
        {(save.error || doSign.error) && !Object.keys(errs).length && <Alert tone="danger">{(save.error ?? doSign.error)!.message}</Alert>}
        {r?.status !== 'signed' && write && (
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => save.mutate()} disabled={!dirty} loading={save.isPending}>{dirty ? 'Salvar rascunho' : 'Rascunho salvo'}</Button>
            {sign ? <Button variant="secondary" disabled={dirty || !r} onClick={() => setConfirm(true)}><FileSignature className="size-4" />Assinar e concluir</Button>
              : <span className="text-[12.5px] text-fg-muted">A assinatura é do médico.</span>}
          </div>
        )}
        {r?.status === 'signed' && (
          <div className="grid gap-3 border-t border-border pt-4">
            <p className="text-sm font-medium text-fg">Adendos</p>
            {r.addenda.map((x) => <div key={x.id} className="rounded-md border border-border bg-surface-muted/50 px-3.5 py-3"><p className="text-sm whitespace-pre-wrap text-fg">{x.text}</p><p className="mt-1 text-[12px] text-fg-muted">{x.authorName} · {dt(x.createdAt)}</p></div>)}
            {write && (
              <form className="grid gap-2" onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
                <textarea aria-label="Novo adendo" value={addendum} onChange={(e) => setAddendum(e.target.value)} rows={2} className="w-full resize-y rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-fg" />
                <div><Button size="sm" type="submit" disabled={addendum.trim().length < 3} loading={add.isPending}>Registrar adendo</Button></div>
              </form>
            )}
          </div>
        )}
      </div>
      <Dialog open={confirm} onClose={() => setConfirm(false)} title="Assinar o registro e concluir a cirurgia?">
        <div className="grid gap-4 p-5 text-sm text-fg-muted">
          <p>O registro fica no prontuário e não muda mais (correções por adendo). A cirurgia passa a “realizada” e o acompanhamento do pós-operatório começa.</p>
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setConfirm(false)}>Voltar</Button><Button loading={doSign.isPending} onClick={() => doSign.mutate()}><Lock className="size-4" />Assinar</Button></div>
        </div>
      </Dialog>
    </Card>
  );
}

function SurgeryCase({ id }: { id: string }) {
  const { data: s } = useQuery(surgeryQuery(id));
  if (!s) return <Spinner className="size-5 text-fg-subtle" label="Carregando" />;
  return (
    <div className="grid gap-5">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold text-fg">Cirurgia · {s.quoteCode}</h3>
        <Badge tone={SURGERY_STATUS[s.status][1]}>{SURGERY_STATUS[s.status][0]}</Badge>
        {s.plan.graftsEstimateMax && <span className="text-[13px] text-fg-muted">Plano: {s.plan.graftsEstimateMin}–{s.plan.graftsEstimateMax} UFs{s.plan.technique ? ` · ${s.plan.technique}` : ''}</span>}
      </div>
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Checklist s={s} />
        <ScheduleCard s={s} />
      </div>
      <RecordCard key={`${s.record?.id ?? 'novo'}-${s.record?.status ?? ''}`} s={s} />
    </div>
  );
}

/** Aba Cirurgia da ficha do paciente. */
export function SurgeryTab({ patientId }: { patientId: string }) {
  const qc = useQueryClient();
  const { write } = usePerms();
  const { data: list } = useQuery(surgeriesQuery(patientId));
  const { data: quotes } = useQuery({ queryKey: ['surgeries', 'quotes', patientId], queryFn: () => api<{ id: string; code: string; acceptedAt: string | null }[]>(`/patients/${patientId}/surgery-quotes`) });
  const [picked, setPicked] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: (quoteId: string) => api<Surgery>('/surgeries', { method: 'POST', body: { quoteId } }),
    onSuccess: (s) => { setPicked(s.id); void qc.invalidateQueries({ queryKey: ['surgeries'] }); },
  });
  const current = picked ?? list?.find((x) => x.status !== 'cancelled')?.id ?? list?.[0]?.id ?? null;
  if (!list) return <Spinner className="size-5 text-fg-subtle" label="Carregando" />;
  return (
    <div className="grid gap-5">
      {(list.length > 1 || (quotes?.length ?? 0) > 0) && (
        <div className="flex flex-wrap items-center gap-2">
          {list.map((x) => <button key={x.id} type="button" onClick={() => setPicked(x.id)} aria-pressed={x.id === current}
            className={cn('h-8 rounded-full border px-3 text-[13px]', x.id === current ? 'border-primary bg-primary-soft text-primary' : 'border-border-strong text-fg-muted')}>{x.quoteCode} · {SURGERY_STATUS[x.status][0]}</button>)}
          {write && quotes?.map((q) => <Button key={q.id} size="sm" variant="secondary" loading={create.isPending} onClick={() => create.mutate(q.id)}><Plus className="size-3.5" />Abrir caso do {q.code}</Button>)}
        </div>
      )}
      {create.error && <Alert tone="danger">{create.error.message}</Alert>}
      {current ? <SurgeryCase key={current} id={current} /> : (
        <Card className="grid place-items-center gap-2 p-10 text-center">
          <p className="font-semibold text-fg">Sem caso cirúrgico</p>
          <p className="max-w-md text-sm text-fg-muted">O caso nasce do orçamento aceito. {quotes?.length ? 'Abra acima.' : 'Este paciente ainda não tem orçamento aceito.'}</p>
        </Card>
      )}
    </div>
  );
}
